import { User } from 'app/types'
import { UserPopup } from './user-popup'
import { Button, YStack, useToastController } from '@my/ui'
import { useAuth } from 'app/provider/auth'
import { useEffect, useState } from 'react'
import { BackHandler } from 'react-native'
import { MuteUsernameModal } from '../mute-username-modal'
import { BlockUsernameModal } from '../block-username-modal'
import { useUnblockUser } from 'app/features/user/screens/blocking-screen/commands'
import { useUnmuteUser } from './commands'
import { ButtonProps } from '@my/ui'
import { useBlockedUserQuery, useMutedUserQuery } from '../../queries'
import { useCreateChat } from './commands/use-create-chat'
import { useRouter } from 'solito/router'

interface UserPopupContainerProps {
  user: Pick<
    User,
    'id' | 'fullName' | 'university' | 'subjectOfStudy' | 'avatar'
  > | null
  onOpenChange: (value: boolean) => void
  open: boolean
}

export const UserPopupContainer = (props: UserPopupContainerProps) => {
  const { user: authUser } = useAuth()
  const toast = useToastController()
  const router = useRouter()
  const [muteUsernameModalOpen, setMuteUsernameModalOpen] = useState(false)
  const [blockUsernameModalOpen, setBlockUsernameModalOpen] = useState(false)

  const [unblockUser, { loading: unblockLoading }] = useUnblockUser()
  const [unmuteUser, { loading: unmuteLoading }] = useUnmuteUser()
  const [createChat, { loading: createChatLoading }] = useCreateChat()

  useEffect(() => {
    const backAction = () => {
      props.onOpenChange(false);
      return props.open
    }

    const backHandler = BackHandler.addEventListener(
      "hardwareBackPress",
      backAction
    );

    return () => backHandler.remove();
  }, [])

  const isViewingSelf = authUser?.id === props.user?.id

  const {
    data: blockedUsersData,
    refetch: refetchBlocked,
    loading: blockedLoading,
  } = useBlockedUserQuery({
    variables: {
      blockeeId: props.user?.id || '',
    },
    skip: !props.user?.id || !props.open || isViewingSelf,
  })

  const {
    data: mutedUsersData,
    refetch: refetchMuted,
    loading: mutedLoading,
  } = useMutedUserQuery({
    variables: {
      muteeId: props.user?.id || '',
    },
    skip: !props.user?.id || !props.open || isViewingSelf,
  })

  const blockedUser = blockedUsersData?.BlockedUsers?.docs || []
  const mutedUser = mutedUsersData?.MutedUsers?.docs || []

  const handleUnblock = async (userId?: string) => {
    if (!userId) return
    await unblockUser({
      variables: {
        blockeeId: userId,
      },
      onCompleted: () => {
        toast.show('User unblocked')
        refetchBlocked()
      },
      onError: () => {
        toast.show('Something went wrong', { burntOptions: { preset: 'error' } })
      },
    })
  }

  const handleUnmute = async (userId?: string) => {
    if (!userId) return
    await unmuteUser({
      variables: {
        muteeId: userId,
      },
      onCompleted: () => {
        toast.show('User unmuted')
        refetchMuted()
      },
      onError: () => {
        toast.show('Something went wrong', { burntOptions: { preset: 'error' } })
      },
    })
  }

  const handleCreateChat = async (userId?: string) => {
    if (!userId) return
    await createChat({
      variables: {
        participants: [userId],
      },
      onCompleted: (data) => {
        const chatId = data?.createChat?.id
        if (chatId) {
          props.onOpenChange(false)
          router.push(`/groups/chat/${chatId}`)
        } else {
          toast.show('Something went wrong', { burntOptions: { preset: 'error' } })
        }
      },
      onError: () => {
        toast.show('Something went wrong', { burntOptions: { preset: 'error' } })
      },
    })
  }

  const isMuted = mutedUser.some((u) => u?.mutee?.id === props.user?.id)
  const canMute = !isMuted && authUser && !isViewingSelf

  const isBlocked = blockedUser.some((u) => u?.blockee?.id === props.user?.id)
  const canBlock = !isBlocked && authUser && !isViewingSelf

  const canCreateChat = authUser && !isViewingSelf

  const optionsLoading = blockedLoading || mutedLoading

  const options: ButtonProps[] = [
    canCreateChat && {
      theme: 'primary',
      children: 'Direct message',
      loading: createChatLoading,
      onPress: () => handleCreateChat(props.user?.id),
    },
    canMute && {
      plain: true,
      outlined: true,
      children: 'Mute user',
      bw: 2,
      focusStyle: { bw: 2 },
      onPress: () => setMuteUsernameModalOpen(true),
    },
    isMuted && {
      plain: true,
      children: 'Unmute user',
      bw: 2,
      focusStyle: { bw: 2 },
      onPress: () => handleUnmute(props.user?.id),
      loading: unmuteLoading,
    },
    canBlock && {
      plain: true,
      children: 'Block user',
      onPress: () => setBlockUsernameModalOpen(true),
    },
    isBlocked && {
      plain: true,
      children: 'Unblock user',
      onPress: () => handleUnblock(props.user?.id),
      loading: unblockLoading,
    },
  ].filter(Boolean) as ButtonProps[]

  return (
    <>
      <UserPopup user={props.user} open={props.open} onOpenChange={props.onOpenChange}>
        {!optionsLoading && (
          <YStack gap="$3.5" pt={options.length > 0 ? '$6' : '$0'}>
            {options.map((option, index) => (
              <Button key={index} {...option} />
            ))}
          </YStack>
        )}
      </UserPopup>
      {props.user && canMute && (
        <MuteUsernameModal
          mutee={props.user}
          open={muteUsernameModalOpen}
          onOpenChange={setMuteUsernameModalOpen}
        />
      )}
      {props.user && canBlock && (
        <BlockUsernameModal
          blockee={props.user}
          open={blockUsernameModalOpen}
          onOpenChange={setBlockUsernameModalOpen}
        />
      )}
    </>
  )
}
